import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useRef, useContext } from 'react';
import DatePicker from 'react-datepicker';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { OpportunityDetail, PatchedOpportunityUpdate } from '../../../../../js/api';
import { CertificationTypeEnum } from '../../../../../js/api/models/CertificationTypeEnum';
import type { OpportunityCreate } from '../../../../../js/api/models/OpportunityCreate';
import { PatchedOpportunityStatusUpdate } from '../../../../../js/api/models/PatchedOpportunityStatusUpdate';
import { Status } from '../../../../../js/api/models/Status';
import { WorkTypeEnum } from '../../../../../js/api/models/WorkTypeEnum';
import { AssignmentsService } from '../../../../../js/api/services/AssignmentsService';
import { CodeDefinitionsService } from '../../../../../js/api/services/CodeDefinitionsService';
import { HealthcareEntitiesService } from '../../../../../js/api/services/HealthcareEntitiesService';
import { WorkflowsService } from '../../../../../js/api/services/WorkflowsService';
import { UnsavedChangesFormPrompt } from '../../../../../js/app/components/UnsavedChangesFormPrompt';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { BoardCertificationComboSelect } from '../../../components/BoardCertificationComboSelect';
import { Button } from '../../../components/button';
import { CheckButton } from '../../../components/CheckButton';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../../components/dialog';
import { Divider } from '../../../components/divider';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Label, Fieldset, Field, Description } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { PageHeader } from '../../../components/PageHeader';
import { SectionHeader } from '../../../components/SectionHeader';
import { ShiftTypeSelector } from '../../../components/shift-type-selector';
import { StatusPill } from '../../../components/StatusPill';
import { Textarea } from '../../../components/textarea';
import { ToggleField } from '../../../components/ToggleField';
import { TooltippedText } from '../../../components/tooltipped-text';
import { Warning } from '../../../components/Warning';
import { parseApiLocalDate, parseFormDate } from '../../../utils/date';
import { reactSelectReset } from '../../../utils/utils';
import { EditFacilityDialog } from '../EditFacilityDialog';
import { EditSpecialtyDialog } from '../EditSpecialtyDialog';
import 'react-datepicker/dist/react-datepicker.css';

const SECTION_CLASSES =
  'tw:bg-white tw:rounded-lg tw:border tw:border-zinc-200 tw:p-6 tw:dark:bg-zinc-700 tw:dark:border tw:dark:border-zinc-400';

const getStatusButtonStyling = (status: any): string => {
  if (status.is_initial) {
    return 'brandgreen1';
  }
  if (status.is_draft) {
    return 'brandgreen1';
  }
  if (status.is_final) {
    return 'stoplight-danger-outline';
  }
  return 'brandgreen1';
};

const opportunityFormSchema = z.object({
  // Basic Information
  facility: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .refine((v) => v != null, { message: 'Home facility is required' }),
  title: z.string().optional(),
  starts_on: z.date().nullable(),
  estimated_duration: z.string(),
  description: z.string().max(255),
  alternate_facilities: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    })
  ),

  // Professional Requirements
  specialty: z
    .object({
      value: z.string(),
      description: z.string(),
    })
    .nullable()
    .refine((v) => v != null, { message: 'Specialty is required' }),
  degrees: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    )
    .min(1, { message: 'At least one degree must be selected' }),
  work_types: z.array(z.string()),
  life_support_certifications: z.array(z.string()),
  board_certifications: z.array(
    z.object({
      value: z.string(),
      boardEligible: z.boolean(),
    })
  ),

  // Eligibility Requirements
  allow_imlc: z.boolean(),
  allow_malpractice_history: z.boolean(),
  years_of_experience: z.coerce
    .number()
    .gte(0, { message: 'Minimum experience must be greater than 0' })
    .nullable(),
  shifts_completed: z.coerce
    .number()
    .gte(0, { message: 'Minimum shifts completed must be greater than 0' })
    .nullable(),
  additional_info: z.string(),

  // Settings
  settings: z.object({
    auto_match: z.boolean(),
    max_accepted_connections: z.coerce
      .number()
      .gte(0, { message: 'Maximum accepted connection must be greater than 0' })
      .nullable(),
    max_rate_day: z.coerce.number().min(0).nullable(),
    max_rate_night: z.coerce.number().min(0).nullable(),
    max_rate_call: z.coerce.number().min(0).nullable(),
    max_rate_24h: z.coerce.number().min(0).nullable(),
  }),

  shift_rate_types: z.array(z.string()),

  // Status
  current_status: z.any().optional(),
});

type OpportunityFormData = z.infer<typeof opportunityFormSchema>;

const DEFAULT_FORM_VALUES: OpportunityFormData = {
  facility: null,
  title: '',
  starts_on: null,
  estimated_duration: '',
  description: '',
  alternate_facilities: [],
  specialty: null,
  degrees: [
    { value: 'MD', label: 'MD' },
    { value: 'DO', label: 'DO' },
  ],
  work_types: Object.values(WorkTypeEnum),
  life_support_certifications: [],
  board_certifications: [],
  allow_imlc: true,
  allow_malpractice_history: true,
  years_of_experience: null,
  shifts_completed: null,
  additional_info: '',
  settings: {
    auto_match: true,
    max_accepted_connections: null,
    max_rate_day: null,
    max_rate_night: null,
    max_rate_call: null,
    max_rate_24h: null,
  },
  shift_rate_types: [],
};

interface ActionButtonsProps {
  onCancel: () => void;
  onDiscard: () => void;
  onSaveDraft: () => void;
  onPublish: () => void;
  onEdit?: () => void;
  isEditMode: boolean;
  isViewMode?: boolean;
  getEditButtonsHtml: () => JSX.Element;
  getViewModeButtonsHtml: () => JSX.Element;
  isReadOnly: boolean;
  isClosed: boolean;
  user?: any;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  onCancel,
  onDiscard,
  onSaveDraft,
  onPublish,
  onEdit,
  isEditMode,
  isViewMode,
  isReadOnly,
  isClosed,
  getEditButtonsHtml,
  getViewModeButtonsHtml,
  user,
}) => {
  return (
    <>
      {isEditMode && !isReadOnly && (
        <Button type="button" color="zinc-outline" onClick={onDiscard}>
          Discard Changes
        </Button>
      )}
      {isEditMode && getEditButtonsHtml()}
      {isEditMode && isClosed && getViewModeButtonsHtml()}
      {isViewMode &&
        onEdit &&
        user?.permissions?.includes('update__assignments__opportunity') &&
        !isClosed && (
          <Button type="button" color="brand-green-outline-on-dark" onClick={onEdit}>
            Edit
          </Button>
        )}
      {isViewMode && getViewModeButtonsHtml()}
      {!isEditMode && !isViewMode && (
        <>
          <Button type="button" color="zinc-outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" color="brand-green-outline-on-dark" onClick={onSaveDraft}>
            Save Draft
          </Button>
          <Button type="button" color="brandgreen1" onClick={onPublish}>
            Publish
          </Button>
        </>
      )}
    </>
  );
};

interface ConfirmationDialogProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: string;
  confirmText?: string;
  cancelText?: string;
}

interface OpportunityFormProps {
  readOnly?: boolean;
  opportunityId?: string;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  show,
  onClose,
  onConfirm,
  title,
  body,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
}) => {
  return (
    <Dialog open={show} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogBody>
        <div dangerouslySetInnerHTML={{ __html: body }} />
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose}>
          {cancelText}
        </Button>
        <Button color="stoplight-danger" onClick={onConfirm}>
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OpportunityForm: React.FC<OpportunityFormProps> = ({
  readOnly: forceReadOnly,
  opportunityId: propOpportunityId,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { user } = useContext(SessionContext);
  const queryClient = useQueryClient();

  const opportunityId = propOpportunityId || params.opportunityId;
  const urlPath = location.pathname;

  const isCreateMode = urlPath.includes('/new');
  const isEditMode = urlPath.includes('/edit');
  const isViewMode = !isCreateMode && !isEditMode && Boolean(opportunityId);

  const searchParams = new URLSearchParams(location.search);
  const duplicateId = searchParams.get('duplicate');

  const form = useForm<OpportunityFormData>({
    resolver: zodResolver(opportunityFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
    register,
  } = form;

  const watchedHomeFacility = watch('facility');
  const watchedSpecialty = watch('specialty');

  const [isCodeDefinitionsLoading, setIsCodeDefinitionsLoading] = useState(true);
  const formRef = useRef<HTMLFormElement>(null);
  const [filteredFacilities, setFilteredFacilities] = useState<any[]>([]);
  const [workTypeOptions, setWorkTypeOptions] = useState<
    Array<{ value: string; description: string }>
  >([]);
  const [certificationTypeOptions, setCertificationTypeOptions] = useState<
    Array<{ value: string; description: string }>
  >([]);
  const [degreeOptions, setDegreeOptions] = useState<Array<{ value: string; description: string }>>(
    []
  );
  const [specialtyOptions, setSpecialtyOptions] = useState<
    Array<{ value: string; description: string }>
  >([]);
  const [boardCertificationOptions, setBoardCertificationOptions] = useState<
    Array<{ value: string; description: string }>
  >([]);
  const [shiftTypeOptions, setShiftTypeOptions] = useState<
    Array<{ key: string; label: string; icon: JSX.Element }>
  >([]);

  const [selectedAdditionalFacility, setSelectedAdditionalFacility] = useState<any | null>(null);

  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState<{
    statusCode: string;
    statusName: string;
  } | null>(null);

  const [showEditFacilityDialog, setShowEditFacilityDialog] = useState(false);
  const [showEditSpecialtyDialog, setShowEditSpecialtyDialog] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const statusUpdateMutation = useMutation({
    mutationFn: async (data: { id: string; statusCode: string }) => {
      const statusUpdateData: PatchedOpportunityStatusUpdate = {
        code: data.statusCode,
      };
      return await AssignmentsService.assignmentsOpportunitiesStatusUpdatePartialUpdate({
        id: data.id,
        requestBody: statusUpdateData,
      });
    },
    onSuccess: (updatedOpportunity) => {
      toast.success('Opportunity status successfully updated');

      const newStatus =
        workflowMetadata?.['assignments.opportunity']?.[updatedOpportunity.current_status.code];
      const wasFinalStatus =
        workflowMetadata?.['assignments.opportunity']?.[watch('current_status')?.code]?.is_final;
      const isNowNonFinal = !newStatus?.is_final;

      if (wasFinalStatus && isNowNonFinal) {
        queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
        return;
      }
      if (updatedOpportunity.need?.id) {
        queryClient.invalidateQueries({ queryKey: ['need', updatedOpportunity.need?.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['staffingNeeds', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
      navigate(`/app/organization/opportunities/${opportunityId}`);
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Failed to update opportunity status' });
    },
  });

  const { data: facilities } = useQuery({
    queryKey: ['healthcareEntityFacilities', 'list', '{}'],
    queryFn: () => HealthcareEntitiesService.healthcareEntitiesFacilitiesList({ limit: 1000 }),
  });

  const { data: facilityTypeFields } = useQuery({
    queryKey: ['facilityTypeFields'],
    queryFn: () => HealthcareEntitiesService.healthcareEntitiesFacilityTypeFieldsList({}),
  });

  const { data: fullFacilityData } = useQuery({
    queryKey: ['facility', watch('facility')?.id],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesRetrieve({
        id: watch('facility')!.id,
      }),
    enabled: showEditFacilityDialog && Boolean(watch('facility')?.id),
  });

  const { data: facilitySpecialtyData } = useQuery({
    queryKey: ['facilitySpecialty', watch('facility')?.id, watch('specialty')?.value],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesList({
        facility: watch('facility')!.id,
      }),
    enabled:
      showEditSpecialtyDialog &&
      Boolean(watch('facility')?.id) &&
      Boolean(watch('specialty')?.value),
  });

  const { data: workflowMetadata } = useQuery({
    queryKey: ['workflowsMeta', 'assignments.opportunity'],
    queryFn: () =>
      WorkflowsService.workflowsMetaRetrieve({
        scope: ['assignments.opportunity'],
      }),
  });

  const { data: existingOpportunity, isLoading: isLoadingOpportunity } = useQuery({
    queryKey: ['opportunity', opportunityId],
    queryFn: () => AssignmentsService.assignmentsOpportunitiesRetrieve({ id: opportunityId! }),
    enabled: (isEditMode || isViewMode) && Boolean(opportunityId),
  });

  const { data: duplicateOpportunity, isLoading: isLoadingDuplicate } = useQuery({
    queryKey: ['opportunity', duplicateId],
    queryFn: () => AssignmentsService.assignmentsOpportunitiesRetrieve({ id: duplicateId! }),
    enabled: Boolean(duplicateId) && !isEditMode,
  });

  const opportunityDisplayTitle = (existingOpportunity?.code || '').trim() || 'Opportunity Details';
  const documentTitle = isCreateMode ? 'Create Opportunity' : opportunityDisplayTitle;
  useDocumentTitle(documentTitle);

  const linkedNeed = (existingOpportunity as OpportunityDetail | undefined)?.need;

  const isReadOnly = React.useMemo(() => {
    if (forceReadOnly !== undefined) {
      return forceReadOnly;
    }

    if (isViewMode) {
      return true;
    }

    if (isEditMode) {
      const hasUpdatePermission = user?.permissions?.includes('update__assignments__opportunity');
      const isFinalStatus =
        watch('current_status')?.code &&
        workflowMetadata?.['assignments.opportunity']?.[watch('current_status').code]?.is_final;
      return !hasUpdatePermission || isFinalStatus;
    }

    return !user?.permissions?.includes('create__assignments__opportunity');
  }, [
    forceReadOnly,
    isViewMode,
    isEditMode,
    user?.permissions,
    watch('current_status'),
    workflowMetadata,
  ]);

  const isClosed = React.useMemo(() => {
    const currentStatusCode = watch('current_status')?.code;
    return Boolean(
      currentStatusCode &&
        workflowMetadata?.['assignments.opportunity']?.[currentStatusCode]?.is_final
    );
  }, [watch('current_status'), workflowMetadata]);

  // techdebt: don't use 'IP' literal
  const isLockedField = React.useMemo(() => {
    const isInProcess =
      watch('current_status')?.code &&
      workflowMetadata?.['assignments.opportunity']?.[watch('current_status').code]?.category ===
        'IP';
    return isReadOnly || isInProcess;
  }, [isReadOnly, watch('current_status'), workflowMetadata]);

  const facilityTypesWithDynamicFields = React.useMemo(() => {
    if (!facilityTypeFields?.results) return {};
    return facilityTypeFields.results.reduce((acc: Record<string, any[]>, item) => {
      acc[item.facility_type] = Array.isArray(item.dynamic_fields) ? item.dynamic_fields : [];
      return acc;
    }, {});
  }, [facilityTypeFields]);

  useEffect(() => {
    if (facilities?.results) {
      const filtered = watchedHomeFacility
        ? facilities.results.filter((facility) => facility.id !== watchedHomeFacility.id)
        : facilities.results;
      setFilteredFacilities(filtered);
    }
  }, [facilities, watchedHomeFacility]);

  useEffect(() => {
    if (watchedHomeFacility) {
      const currentAdditionalFacilities = watch('alternate_facilities') || [];
      const filtered = currentAdditionalFacilities.filter(
        (f: any) => f.id !== watchedHomeFacility.id
      );
      setValue('alternate_facilities', filtered);
    }
  }, [watchedHomeFacility, watch, setValue]);

  // Create mode
  useEffect(() => {
    if (isCreateMode && !duplicateId) {
      reset(DEFAULT_FORM_VALUES);
    }
  }, [isCreateMode, duplicateId, reset]);

  // Get code definitions
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsCodeDefinitionsLoading(true);
      try {
        const categories = [
          'WorkType',
          'CertificationType',
          'Degree',
          'Specialty',
          'BoardCertificationType',
          'ShiftRateTypes',
          'ShiftRateTypeIcons',
        ];
        // TODO: use useCodeDefinitionsByCategories hook instead
        const allCodeDefinitions = await CodeDefinitionsService.codeDefinitionsList({
          categoryIn: [categories.join(',')],
          ordering: 'sequence',
        });
        const workTypes = allCodeDefinitions
          .filter((def) => def.category === 'WorkType')
          .map((def) => ({ value: def.code, description: def.title }));
        const certificationTypes = allCodeDefinitions
          .filter((def) => def.category === 'CertificationType')
          .map((def) => ({ value: def.code, description: def.title }));
        const degrees = allCodeDefinitions
          .filter((def) => def.category === 'Degree')
          .map((def) => ({ value: def.code, description: def.title }));
        const specialties = allCodeDefinitions
          .filter((def) => def.category === 'Specialty')
          .map((def) => ({ value: def.code, description: def.title }));
        const boardCertificationOptions = allCodeDefinitions
          .filter((def) => def.category === 'BoardCertificationType')
          .map((def) => ({ value: def.code, description: def.title }));
        const shiftTypes = allCodeDefinitions
          .filter((def) => def.category === 'ShiftRateTypes')
          .map((def) => ({ code: def.code, title: def.title }));
        const shiftIcons = allCodeDefinitions
          .filter((def) => def.category === 'ShiftRateTypeIcons')
          .map((def) => ({ code: def.code, icon: def.title }));
        const combinedShiftTypes = shiftTypes.map((type) => {
          const icon = shiftIcons.find((i) => i.code === type.code);
          return {
            key: type.code.toLowerCase(),
            label: type.title,
            icon: <i className={`${icon?.icon || 'fas fa-question'} tw:w-6 tw:h-6`} />,
          };
        });
        setWorkTypeOptions(workTypes);
        setCertificationTypeOptions(certificationTypes);
        setDegreeOptions(degrees);
        setSpecialtyOptions(specialties);
        setBoardCertificationOptions(boardCertificationOptions);
        setShiftTypeOptions(combinedShiftTypes);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setIsCodeDefinitionsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const FIELD_TRANSFORMATIONS = React.useMemo(() => {
    const transformations: Record<
      string,
      { formToApi: (value: any) => any; apiToForm: (value: any) => any }
    > = {};

    transformations.facility = {
      formToApi: (value: any) => value?.id || null,
      apiToForm: (value: any) => value || null,
    };

    transformations.need = {
      formToApi: (value: any) => null,
      apiToForm: (value: any) => value || null,
    };

    transformations.starts_on = {
      formToApi: (value: Date | null) => {
        return parseFormDate(value);
      },
      apiToForm: (value: string | null | undefined) => {
        return parseApiLocalDate(value);
      },
    };

    transformations.degrees = {
      formToApi: (value: any[]) => value.map((degree) => degree.value) || [],
      apiToForm: (value: string[]) =>
        value?.map((degree) => ({ value: degree, label: degree })) || [
          { value: 'MD', label: 'MD' },
          { value: 'DO', label: 'DO' },
        ],
    };

    transformations.specialty = {
      formToApi: (value: any) => value?.value,
      apiToForm: (value: string) => {
        if (!value) return null;
        if (specialtyOptions.length === 0) {
          return { value, description: value };
        }
        return specialtyOptions.find((s) => s.value === value) || null;
      },
    };

    return transformations;
  }, [specialtyOptions]);

  const formToApi = (formData: any): OpportunityCreate | PatchedOpportunityUpdate => {
    const result: any = { ...formData };

    Object.entries(FIELD_TRANSFORMATIONS).forEach(([fieldName, transformation]) => {
      if (formData[fieldName] !== undefined) {
        result[fieldName] = transformation.formToApi(formData[fieldName]);
      }
    });

    return result;
  };

  const diffFormToApi = (formData: any, dirtyFields: any): Partial<PatchedOpportunityUpdate> => {
    const isFieldDirty = (fieldPath: string): boolean => {
      const pathParts = fieldPath.split('.');
      let current = dirtyFields;
      for (const part of pathParts) {
        if (!current || !current[part]) return false;
        current = current[part];
      }
      return true;
    };

    const transformedData = formToApi(formData);

    const result: any = {};
    Object.keys(transformedData).forEach((fieldName) => {
      if (isFieldDirty(fieldName)) {
        result[fieldName] = transformedData[fieldName];
      }
    });

    return result;
  };

  const apiToForm = React.useCallback(
    (apiData: any): Partial<OpportunityFormData> => {
      const result: any = { ...apiData };

      Object.entries(FIELD_TRANSFORMATIONS).forEach(([fieldName, transformation]) => {
        if (apiData[fieldName] !== undefined) {
          result[fieldName] = transformation.apiToForm(apiData[fieldName]);
        }
      });

      return result;
    },
    [FIELD_TRANSFORMATIONS]
  );

  // status/mode checks
  const isDataReady =
    (!isEditMode && !isViewMode) ||
    (existingOpportunity &&
      facilities?.results &&
      specialtyOptions.length > 0 &&
      !isCodeDefinitionsLoading);

  const isDuplicateDataReady =
    Boolean(duplicateId) &&
    !isEditMode &&
    duplicateOpportunity &&
    facilities?.results &&
    specialtyOptions.length > 0 &&
    !isCodeDefinitionsLoading;

  const isFormPopulated = (!isEditMode && !isViewMode) || !!watch('current_status')?.code;

  const isLoading =
    ((isEditMode || isViewMode) && (isLoadingOpportunity || !isDataReady || !isFormPopulated)) ||
    (!isEditMode &&
      !isViewMode &&
      Boolean(duplicateId) &&
      (isLoadingDuplicate || !isDuplicateDataReady || !isFormPopulated)) ||
    (!isEditMode && !isViewMode && !duplicateId && (!isDataReady || !isFormPopulated));

  // Edit Mode
  useEffect(() => {
    if ((isEditMode || isViewMode) && isDataReady && existingOpportunity && facilities) {
      const formData = apiToForm(existingOpportunity);
      reset(formData);
    }
  }, [isEditMode, isViewMode, isDataReady, existingOpportunity, facilities, reset, apiToForm]);

  // Duplicate mode
  useEffect(() => {
    if (!isEditMode && isDuplicateDataReady && duplicateOpportunity && facilities) {
      const formData = apiToForm(duplicateOpportunity);

      formData.current_status = {} as Status;

      reset(formData);
    }
  }, [isEditMode, isDuplicateDataReady, duplicateOpportunity, facilities, reset, apiToForm]);

  const getDraftStatus = () => {
    if (!workflowMetadata?.['assignments.opportunity']) return null;
    const statuses = workflowMetadata?.['assignments.opportunity'];
    const draftStatus = Object.entries(statuses).find(
      ([_, status]) => (status as any).is_draft === true
    );
    return draftStatus ? draftStatus[0] : null;
  };

  const getInitialStatus = () => {
    if (!workflowMetadata?.['assignments.opportunity']) return null;
    const statuses = workflowMetadata?.['assignments.opportunity'];
    const initialStatus = Object.entries(statuses).find(
      ([_, status]) => (status as any).is_default === true
    );
    return initialStatus ? initialStatus[0] : null;
  };

  const transformFormData = (): OpportunityCreate | PatchedOpportunityUpdate => {
    const currentValues = watch();

    if (isEditMode && opportunityId) {
      // eslint-disable-next-line sonarjs/cognitive-complexity
      return diffFormToApi(currentValues, form.formState.dirtyFields);
    } else {
      const result = formToApi(currentValues);
      (result as OpportunityCreate).current_status = '';
      return result;
    }
  };

  const onInvalid = (formErrors: any) => {
    const messages: string[] = Object.values(formErrors)
      .map((e: any) => e?.message)
      .filter(Boolean);

    Object.entries(formErrors).forEach(([fieldName, error]: [string, any]) => {
      if (error?.message) {
        setError(fieldName as keyof OpportunityFormData, {
          type: 'manual',
          message: error.message,
        });
      }
    });

    if (messages.length) {
      toast.error(
        <div>
          <div className="tw:font-medium tw:mb-1">Please fix the following errors:</div>
          <ul className="tw:list-disc tw:list-inside">
            {messages.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  const handleCancel = () => {
    // TODO: should we circumvent unsaved changes alert here?
    // setIsDiscarding(true);
    navigate('/app/organization/opportunities');
  };

  const handleDiscard = () => {
    if (opportunityId) {
      setIsDiscarding(true);
      navigate(`/app/organization/opportunities/${opportunityId}`);
    }
  };

  const handleEdit = () => {
    if (opportunityId) {
      navigate(`/app/organization/opportunities/${opportunityId}/edit`);
    }
  };

  const createOpportunityMutation = useMutation<OpportunityDetail, Error, OpportunityCreate>({
    mutationFn: async (data: OpportunityCreate): Promise<OpportunityDetail> => {
      /* eslint-disable sonarjs/prefer-immediate-return */
      const response = await AssignmentsService.assignmentsOpportunitiesCreate({
        requestBody: data as OpportunityCreate,
      });
      return response;
    },
    onSuccess: (createdOpportunity: OpportunityDetail) => {
      toast.success('Opportunity created successfully');
      navigate(`/app/organization/opportunities/${createdOpportunity.id}`);
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Error creating opportunity' });
    },
  });

  const updateOpportunityMutation = useMutation({
    mutationFn: async (data: PatchedOpportunityUpdate) => {
      await AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId!,
        requestBody: data as PatchedOpportunityUpdate,
      });
    },
    onSuccess: () => {
      toast.success('Opportunity updated successfully');
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
      navigate(`/app/organization/opportunities/${opportunityId}`);
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Error updating opportunity' });
    },
  });

  const submitOpportunity = (opportunityData: OpportunityCreate | PatchedOpportunityUpdate) => {
    if (isEditMode && opportunityId) {
      updateOpportunityMutation.mutate(opportunityData as PatchedOpportunityUpdate);
    } else {
      createOpportunityMutation.mutate(opportunityData as OpportunityCreate);
    }
  };

  const handleSave = handleSubmit(() => {
    const transformedData = transformFormData();
    submitOpportunity(transformedData);
    reset(watch());
  }, onInvalid);

  const handleSaveDraft = handleSubmit(() => {
    const transformedData = transformFormData();
    const draftStatus = getDraftStatus();
    if (!draftStatus) {
      toast.error('Could not find draft status');
      return;
    }
    if ('current_status' in transformedData) {
      (transformedData as OpportunityCreate).current_status = draftStatus;
    }
    submitOpportunity(transformedData);
    reset(watch());
  }, onInvalid);

  const handlePublish = handleSubmit(() => {
    const transformedData = transformFormData();
    const initialStatus = getInitialStatus();
    if (!initialStatus) {
      toast.error('Could not find default status');
      return;
    }
    if ('current_status' in transformedData) {
      (transformedData as OpportunityCreate).current_status = initialStatus;
    }
    submitOpportunity(transformedData);
    reset(watch());
  }, onInvalid);

  const handleStatusUpdate = (status: any) => {
    if (!opportunityId) {
      toast.error('Opportunity ID not found');
      return;
    }

    if (status.is_final) {
      setPendingStatusUpdate({
        statusCode: status.code,
        statusName: status.title || status.verb,
      });
      setShowStatusConfirmation(true);
    } else {
      statusUpdateMutation.mutate({
        id: opportunityId,
        statusCode: status.code,
      });
    }
  };

  const confirmStatusUpdate = () => {
    if (pendingStatusUpdate && opportunityId) {
      statusUpdateMutation.mutate({
        id: opportunityId,
        statusCode: pendingStatusUpdate.statusCode,
      });
    }
    setShowStatusConfirmation(false);
    setPendingStatusUpdate(null);
  };

  const cancelStatusUpdate = () => {
    setShowStatusConfirmation(false);
    setPendingStatusUpdate(null);
  };

  const getEditButtonsHtml = () => {
    if (isEditMode && isLoading) {
      return <></>;
    }
    return (
      <>
        {!isReadOnly && (
          <Button type="button" color="brandgreen1" onClick={handleSave}>
            Save
          </Button>
        )}
      </>
    );
  };

  const getViewModeButtonsHtml = () => {
    if (isLoading) {
      return <></>;
    }
    const currentStatusCode = watch('current_status')?.code;
    const allowedTransitions =
      workflowMetadata?.['assignments.opportunity']?.[currentStatusCode]?.allowed_transitions;
    const allowedStatuses = allowedTransitions?.map(
      (transitionCode) => workflowMetadata?.['assignments.opportunity']?.[transitionCode]
    );

    return (
      <>
        {allowedStatuses?.map((status) =>
          !status.set_by_healthcare_entity ? (
            <></>
          ) : (
            <Button
              key={status.code}
              color={getStatusButtonStyling(status)}
              onClick={() => handleStatusUpdate(status)}
              disabled={statusUpdateMutation.isLoading}
            >
              {status.verb}
            </Button>
          )
        )}
      </>
    );
  };

  const actionButtonsElement = (
    <ActionButtons
      onCancel={handleCancel}
      onDiscard={handleDiscard}
      onSaveDraft={handleSaveDraft}
      onPublish={handlePublish}
      onEdit={handleEdit}
      isEditMode={isEditMode}
      isViewMode={isViewMode}
      isReadOnly={isReadOnly}
      isClosed={isClosed}
      getEditButtonsHtml={getEditButtonsHtml}
      getViewModeButtonsHtml={getViewModeButtonsHtml}
      user={user}
    />
  );

  const hasLinkedNeed = (isViewMode || isEditMode) && linkedNeed;

  return (
    <>
      {isLoading && (
        <div className="tw:flex tw:items-center tw:justify-center tw:min-h-screen">
          <div className="tw:text-center">
            <div className="tw:animate-spin tw:rounded-full tw:h-12 tw:w-12 tw:border-b-2 tw:border-brand-green-0 tw:mx-auto tw:mb-4" />
            <p className="tw:text-zinc-600 tw:dark:text-zinc-400">
              {isEditMode ? 'Loading opportunity...' : 'Loading...'}
            </p>
          </div>
        </div>
      )}
      {!isLoading && (
        <>
          <PageHeader
            backUrl="/app/organization/opportunities"
            title={
              isViewMode
                ? 'Opportunity: ' + opportunityDisplayTitle
                : isEditMode
                  ? 'Edit Opportunity: ' + opportunityDisplayTitle
                  : duplicateId
                    ? 'Duplicate Opportunity'
                    : 'Create Opportunity'
            }
            buttonHtml={actionButtonsElement}
          />
          <main className="tw:max-w-6xl tw:mx-auto tw:px-6 tw:pt-3 tw:pb-8">
            {isEditMode && isReadOnly && watch('current_status')?.code && (
              <Warning body="Reopen this opportunity if you wish to edit it." />
            )}
            <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
              <div className="tw:space-y-4">
                {/* Basic Information Section */}
                <section className={SECTION_CLASSES}>
                  <div className="tw:flex tw:items-start tw:justify-between">
                    {/* Left side: header + sub-link stacked */}
                    <div className="tw:flex tw:flex-col">
                      <SectionHeader
                        headerText="Basic Information"
                        icon={
                          <i className="fas fa-info-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                        }
                        className={hasLinkedNeed ? 'tw:!mb-0' : ''}
                      />
                      {hasLinkedNeed && (
                        <div className="tw:mb-4">
                          <button
                            type="button"
                            onClick={() => navigate(`/app/organization/needs/${linkedNeed.id}`)}
                            className="tw:mt-0 tw:!text-sm tw:text-brand-green-0 hover:tw:underline tw:text-left tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:dark:text-brand-green-1"
                          >
                            <i className="far fa-arrow-up-right-from-square tw:mr-2" />
                            Linked Need: {linkedNeed.code}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Right side: status pill */}
                    {watch('current_status') && watch('current_status').title && (
                      <StatusPill
                        status={watch('current_status').title}
                        category={watch('current_status').category}
                      />
                    )}
                  </div>
                  <Fieldset>
                    <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                      <Field>
                        <div className="tw:flex tw:items-center tw:gap-2">
                          <Label>
                            Home Facility <span className="tw:text-stoplight-danger">*</span>
                          </Label>
                          {watchedHomeFacility &&
                            user?.permissions?.includes('update__assignments__opportunity') && (
                              <button
                                type="button"
                                title="Edit Chosen Facility Details"
                                onClick={() => setShowEditFacilityDialog(true)}
                                className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-1/90"
                              >
                                <i className="fas fa-pen-to-square tw:h-4 tw:w-4" />
                              </button>
                            )}
                        </div>
                        <Controller
                          name="facility"
                          control={control}
                          render={({ field }) => (
                            <Select
                              styles={reactSelectReset}
                              placeholder="Start typing or select from list..."
                              options={
                                facilities?.results?.map((facility) => ({
                                  value: facility,
                                  label: facility.name,
                                })) || []
                              }
                              value={
                                field.value
                                  ? {
                                      value: field.value,
                                      label: field.value.name,
                                    }
                                  : null
                              }
                              onChange={(selectedOption) =>
                                field.onChange(selectedOption?.value || null)
                              }
                              isDisabled={isLockedField}
                            />
                          )}
                        />
                        {errors.facility && (
                          <FieldValidationErrorMessage message={errors.facility.message} />
                        )}
                      </Field>
                      <Field>
                        <Label>Opportunity Name</Label>
                        <Input
                          {...register('title')}
                          placeholder="Autogenerated if left blank"
                          disabled={isReadOnly}
                        />
                      </Field>
                    </div>
                    <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                      <Field>
                        <Label>Start Date</Label>
                        <Controller
                          name="starts_on"
                          control={control}
                          render={({ field }) => (
                            <DatePicker
                              className="datepicker-input"
                              wrapperClassName="datepicker-wrapper"
                              selected={field.value}
                              onChange={field.onChange}
                              placeholderText="Select start date"
                              disabled={isReadOnly}
                            />
                          )}
                        />
                      </Field>
                      <Field>
                        <Label>Estimated Duration</Label>
                        <Input
                          {...register('estimated_duration')}
                          placeholder="E.g. 6 months or 3 weeks or 1 year"
                          disabled={isReadOnly}
                        />
                      </Field>
                    </div>
                    <div className="tw:mb-4">
                      <Field>
                        <Label>Description</Label>
                        <Textarea
                          {...register('description')}
                          placeholder="Brief description of the opportunity..."
                          maxLength={255}
                          disabled={isReadOnly}
                        />
                        <div className="tw:flex tw:justify-end tw:text-sm tw:text-zinc-500 tw:mt-1 tw:dark:text-zinc-400">
                          {watch('description')?.length || 0}/255
                        </div>
                      </Field>
                    </div>
                    <div className="tw:mb-4">
                      <Field>
                        <Label>Additional Facilities</Label>
                        <Description>
                          Are there related facilities you wish to connect the provider with as part
                          of this opportunity?
                        </Description>
                        <Controller
                          name="alternate_facilities"
                          control={control}
                          render={({ field }) => (
                            <Select
                              styles={reactSelectReset}
                              placeholder="Start typing or select from list..."
                              options={filteredFacilities
                                .filter(
                                  (fac) =>
                                    !(field.value || []).some((sel: any) => sel.id === fac.id)
                                )
                                .map((facility) => ({
                                  value: facility,
                                  label: facility.name,
                                }))}
                              value={
                                selectedAdditionalFacility
                                  ? {
                                      value: selectedAdditionalFacility,
                                      label: selectedAdditionalFacility.name,
                                    }
                                  : null
                              }
                              onChange={(selectedOption) => {
                                if (
                                  selectedOption?.value &&
                                  !(field.value || []).some(
                                    (f: any) => f.id === selectedOption.value.id
                                  )
                                ) {
                                  const currentValue = field.value || [];
                                  field.onChange([...currentValue, selectedOption.value]);
                                }
                                setSelectedAdditionalFacility(null);
                              }}
                              isDisabled={isLockedField}
                            />
                          )}
                        />
                        <Controller
                          name="alternate_facilities"
                          control={control}
                          render={({ field }) => (
                            <div className="tw:space-y-2 tw:mt-2">
                              {(field.value || []).map((facility: any) => (
                                <div
                                  key={facility.id}
                                  className="tw:flex tw:items-center tw:gap-2 tw:p-2 tw:bg-brand-green-0/5 tw:border tw:border-brand-green-0 tw:rounded-lg tw:dark:border-brand-green-1 tw:dark:bg-white/5"
                                >
                                  {!isReadOnly && !isLockedField && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const currentValue = field.value || [];
                                        field.onChange(
                                          currentValue.filter((f: any) => f.id !== facility.id)
                                        );
                                      }}
                                      className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-1/90"
                                    >
                                      <i className="fat fa-xmark fa-md tw:h-4 tw:w-4 tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-1/90" />
                                    </button>
                                  )}
                                  <span className="tw:flex-1 tw:text-brand-green-0 tw:dark:text-brand-green-1">
                                    {facility.name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        />
                      </Field>
                    </div>
                  </Fieldset>
                </section>

                {/* Professional Requirements Section */}
                <section className={SECTION_CLASSES}>
                  <SectionHeader
                    headerText="Professional Requirements"
                    icon={
                      <i className="fas fa-stethoscope tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <Fieldset>
                    <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                      <Field>
                        <div className="tw:flex tw:items-center tw:gap-2">
                          <Label>
                            Specialty <span className="tw:text-stoplight-danger">*</span>
                          </Label>
                          {watchedSpecialty &&
                            watchedHomeFacility &&
                            isViewMode &&
                            user?.permissions?.includes('update__assignments__opportunity') && (
                              <button
                                type="button"
                                title="Edit Chosen Specialty Details"
                                onClick={() => setShowEditSpecialtyDialog(true)}
                                className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-1/90"
                              >
                                <i className="fas fa-pen-to-square tw:h-4 tw:w-4" />
                              </button>
                            )}
                        </div>
                        <Description>Select one</Description>
                        <Controller
                          name="specialty"
                          control={control}
                          render={({ field }) => (
                            <Select
                              styles={reactSelectReset}
                              placeholder="Start typing or select from list..."
                              options={specialtyOptions.map((option) => ({
                                value: option,
                                label: option.description,
                              }))}
                              value={
                                field.value
                                  ? {
                                      value: field.value,
                                      label: field.value.description,
                                    }
                                  : null
                              }
                              onChange={(selectedOption) =>
                                field.onChange(selectedOption?.value || null)
                              }
                              isDisabled={isLockedField}
                            />
                          )}
                        />
                        {errors.specialty && (
                          <FieldValidationErrorMessage message={errors.specialty.message} />
                        )}
                      </Field>
                      <Field>
                        <Label>
                          Degree <span className="tw:text-stoplight-danger">*</span>
                        </Label>
                        <Description>Select all that apply</Description>
                        <Controller
                          name="degrees"
                          control={control}
                          render={({ field }) => (
                            <Select
                              styles={reactSelectReset}
                              placeholder="Start typing or select from list..."
                              options={degreeOptions.map((option) => ({
                                value: option.value,
                                label: option.description,
                              }))}
                              isMulti
                              value={field.value}
                              onChange={field.onChange}
                              isDisabled={isReadOnly}
                            />
                          )}
                        />
                        {errors.degrees && (
                          <FieldValidationErrorMessage message={errors.degrees.message} />
                        )}
                      </Field>
                    </div>
                    <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:mb-4">
                      <Field className="tw:col-span-1">
                        <Label>Availability</Label>
                        <Description>Select all that apply</Description>
                        <Controller
                          name="work_types"
                          control={control}
                          render={({ field }) => (
                            <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4 tw:w-full">
                              {workTypeOptions?.map((option) => (
                                <CheckButton
                                  key={`work-type-${option.value}`}
                                  value={option.value}
                                  label={option.value}
                                  description={option.description}
                                  name="workType"
                                  checked={field.value?.includes(option.value as WorkTypeEnum)}
                                  onClick={() => {
                                    if (!isReadOnly) {
                                      const newValue = field.value.includes(
                                        option.value as WorkTypeEnum
                                      )
                                        ? field.value.filter((type) => type !== option.value)
                                        : [...field.value, option.value];
                                      field.onChange(newValue);
                                    }
                                  }}
                                  disabled={isReadOnly}
                                />
                              ))}
                            </div>
                          )}
                        />
                      </Field>
                      <Divider />
                      <Field className="tw:col-span-1">
                        <Label className="tw:mb-3">Life Support Certifications</Label>
                        <Controller
                          name="life_support_certifications"
                          control={control}
                          render={({ field }) => (
                            <div className="tw:grid tw:grid-cols-2 tw:md:grid-cols-6 tw:gap-4 tw:w-full">
                              {certificationTypeOptions?.map((option) => (
                                <CheckButton
                                  key={option.value}
                                  value={option.value}
                                  label={option.value}
                                  name="certificationType"
                                  checked={field.value?.includes(
                                    option.value as CertificationTypeEnum
                                  )}
                                  onClick={() => {
                                    if (!isReadOnly) {
                                      const newValue = field.value.includes(
                                        option.value as CertificationTypeEnum
                                      )
                                        ? field.value.filter((type) => type !== option.value)
                                        : [...field.value, option.value as CertificationTypeEnum];
                                      field.onChange(newValue);
                                    }
                                  }}
                                  disabled={isReadOnly}
                                />
                              ))}
                            </div>
                          )}
                        />
                      </Field>
                      <Divider />
                      <Field>
                        <Label>Board Certifications</Label>
                        <Description>
                          Select board certifications and indicate if board eligible is acceptable
                        </Description>
                        <Controller
                          name="board_certifications"
                          control={control}
                          render={({ field }) => (
                            <BoardCertificationComboSelect
                              options={boardCertificationOptions}
                              initialValue={field.value}
                              onChange={(certifications) => {
                                if (!isReadOnly) {
                                  field.onChange(certifications);
                                }
                              }}
                              disabled={isReadOnly}
                            />
                          )}
                        />
                      </Field>
                    </div>
                  </Fieldset>
                </section>

                {/* Eligibility Requirements Section */}
                <section className={SECTION_CLASSES}>
                  <SectionHeader
                    headerText="Eligibility Requirements"
                    icon={
                      <i className="fas fa-check-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <Fieldset>
                    <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                      <Controller
                        name="allow_imlc"
                        control={control}
                        render={({ field }) => (
                          <ToggleField
                            title="IMLC Provider"
                            description="Open to providers not licensed in the state but that hold compact license"
                            checked={field.value}
                            onChange={(checked) => {
                              if (!isReadOnly) {
                                field.onChange(checked);
                              }
                            }}
                            disabled={isReadOnly}
                          />
                        )}
                      />
                      <Controller
                        name="allow_malpractice_history"
                        control={control}
                        render={({ field }) => (
                          <ToggleField
                            title="Allow Malpractice History"
                            description="Accept providers with malpractice history"
                            checked={field.value}
                            onChange={(checked) => {
                              if (!isReadOnly) {
                                field.onChange(checked);
                              }
                            }}
                            disabled={isReadOnly}
                          />
                        )}
                      />
                    </div>
                    <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                      <Field>
                        <Label>Years of Experience</Label>
                        <Description>
                          What is the minimum years of experience you are looking for?
                        </Description>
                        {/* TODO: instead of type=text and using zod to coerce to number, we should convert this
                        to a number input and use setValueAs in the register callback to convert to Number
                        See how facilityRates.* fields are handled in CreateConnectionDialog.tsx */}
                        <Controller
                          name="years_of_experience"
                          control={control}
                          render={({ field }) => (
                            <Input
                              name={field.name}
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value);
                                field.onChange(value);
                              }}
                              onBlur={field.onBlur}
                              value={field.value?.toString() ?? ''}
                              type="text"
                              disabled={isReadOnly}
                              inputMode="numeric"
                            />
                          )}
                        />
                        {errors.years_of_experience && (
                          <FieldValidationErrorMessage
                            message={errors.years_of_experience.message}
                          />
                        )}
                      </Field>
                      <Field>
                        <Label>Minimum Shifts Completed</Label>
                        <Description>
                          Is there a minimum number of completed shifts to qualify for this
                          opportunity?
                        </Description>
                        {/* TODO: instead of type=text and using zod to coerce to number, we should convert this
                        to a number input and use setValueAs in the register callback to convert to Number
                        See how facilityRates.* fields are handled in CreateConnectionDialog.tsx */}
                        <Controller
                          name="shifts_completed"
                          control={control}
                          render={({ field }) => (
                            <Input
                              name={field.name}
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value);
                                field.onChange(value);
                              }}
                              onBlur={field.onBlur}
                              value={field.value?.toString() ?? ''}
                              type="text"
                              disabled={isReadOnly}
                              inputMode="numeric"
                            />
                          )}
                        />
                        {errors.shifts_completed && (
                          <FieldValidationErrorMessage message={errors.shifts_completed.message} />
                        )}
                      </Field>
                    </div>
                    <div className="tw:mb-4">
                      <Field>
                        <Label>Additional Information</Label>
                        <Controller
                          name="additional_info"
                          control={control}
                          render={({ field }) => (
                            <Textarea
                              {...field}
                              placeholder="E.g. Must be able to work nights and weekends, temps not allowed, etc."
                              disabled={isReadOnly}
                            />
                          )}
                        />
                      </Field>
                    </div>
                  </Fieldset>
                </section>

                {/* Opportunity Settings Section */}
                <section className={SECTION_CLASSES}>
                  <SectionHeader
                    headerText="Opportunity Settings"
                    icon={
                      <i className="fas fa-cog tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <Fieldset>
                    <div className="tw:grid tw:grid-cols-1 tw:mb-4">
                      <Field>
                        <Description>
                          Click to select the shift types for this opportunity and then set maximum
                          rate, if necessary
                        </Description>
                        <Controller
                          name="shift_rate_types"
                          control={control}
                          render={({ field }) => (
                            <ShiftTypeSelector
                              options={shiftTypeOptions}
                              initialValue={field.value}
                              onChange={(selectedTypes) => {
                                if (!isReadOnly) {
                                  field.onChange(selectedTypes);
                                }
                              }}
                              disabled={isReadOnly}
                            />
                          )}
                        />

                        {/* Individual rate input fields */}
                        <div
                          className={`tw:grid tw:gap-4 tw:w-full tw:mt-6 sm:tw:grid-cols-${shiftTypeOptions.length} tw:grid-cols-1`}
                          style={{ gridTemplateColumns: `repeat(${shiftTypeOptions.length}, 1fr)` }}
                        >
                          {shiftTypeOptions.map((opt) => {
                            const selected = watch('shift_rate_types')?.includes(opt.key);
                            const rateFieldName = `settings.max_rate_${opt.key}`;

                            return selected ? (
                              <Field key={opt.key} className="tw:mb-0">
                                <Label>Maximum {opt.label} Rate</Label>
                                {/* TODO: instead of type=text and using zod to coerce to number, we should convert this
                                to a number input and use setValueAs in the register callback to convert to Number
                                See how facilityRates.* fields are handled in CreateConnectionDialog.tsx */}
                                <Controller
                                  name={rateFieldName as any}
                                  control={control}
                                  render={({ field }) => (
                                    <div className="tw:relative">
                                      <Input
                                        type="text"
                                        inputMode="numeric"
                                        value={field.value?.toString() ?? ''}
                                        onChange={(e) => {
                                          const value =
                                            e.target.value === '' ? null : Number(e.target.value);
                                          field.onChange(value);
                                        }}
                                        disabled={isReadOnly}
                                      />
                                      <span className="tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-gray-500 tw:dark:text-zinc-300 tw:text-sm">
                                        /hr
                                      </span>
                                    </div>
                                  )}
                                />
                              </Field>
                            ) : (
                              <div key={opt.key} />
                            );
                          })}
                        </div>
                      </Field>
                    </div>
                    <Divider />

                    <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                      <Controller
                        name="settings.auto_match"
                        control={control}
                        render={({ field }) => (
                          <ToggleField
                            title="Auto Match"
                            description="Automatically monitor and match providers that fit criteria and send connections."
                            tooltipContent="When turned on, matching will run on initial publish (with 5 minute delay) and twice daily while open. Note: We recommend setting the Maximum Accepted Providers value when using Auto Match."
                            checked={field.value ?? true}
                            onChange={(checked) => {
                              if (!isReadOnly) {
                                field.onChange(checked);
                              }
                            }}
                            disabled={isReadOnly}
                          />
                        )}
                      />
                      <Field>
                        <Label>
                          Maximum Accepted Providers{' '}
                          <TooltippedText
                            tooltipContent={
                              'After this many provider acceptances have been received, the opportunity will be closed to new connections.  Also, any outstanding requests to other providers will automatically be cancelled.'
                            }
                          >
                            <i className="far fa-circle-info tw:text-brand-green-0" />
                          </TooltippedText>
                        </Label>
                        {/* TODO: instead of type=text and using zod to coerce to number, we should convert this
                        to a number input and use setValueAs in the register callback to convert to Number
                        See how facilityRates.* fields are handled in CreateConnectionDialog.tsx */}
                        <Controller
                          name="settings.max_accepted_connections"
                          control={control}
                          render={({ field }) => (
                            <Input
                              name={field.name}
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value);
                                field.onChange(value);
                              }}
                              onBlur={field.onBlur}
                              value={field.value?.toString() ?? ''}
                              placeholder="Leave blank for unlimited..."
                              type="text"
                              inputMode="numeric"
                              disabled={isReadOnly}
                            />
                          )}
                        />
                        {errors.settings?.max_accepted_connections && (
                          <FieldValidationErrorMessage
                            message={errors.settings.max_accepted_connections.message}
                          />
                        )}
                      </Field>
                    </div>
                  </Fieldset>
                </section>
              </div>
              <div className="tw:flex tw:flex-wrap tw:items-center tw:align-center tw:justify-end tw:gap-2 tw:mt-8">
                {actionButtonsElement}
              </div>
            </form>
          </main>
        </>
      )}

      <ConfirmationDialog
        show={showStatusConfirmation}
        onClose={cancelStatusUpdate}
        onConfirm={confirmStatusUpdate}
        title="Confirm Status Update"
        body={`<p>Are you sure you want to change the opportunity status to "${pendingStatusUpdate?.statusName}"?</p><p class="tw:text-stoplight-danger">This action will close all pending connection requests.</p>`}
        confirmText="Close Opportunity"
        cancelText="Cancel"
      />

      <EditFacilityDialog
        open={showEditFacilityDialog}
        disableNameEdit={true}
        onClose={() => setShowEditFacilityDialog(false)}
        facilityId={watchedHomeFacility?.id || ''}
        initialData={fullFacilityData || watchedHomeFacility || {}}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['healthcareEntityFacilities'] });
          setShowEditFacilityDialog(false);
        }}
        facilityTypesWithDynamicFields={facilityTypesWithDynamicFields}
      />

      {facilitySpecialtyData && (
        <EditSpecialtyDialog
          open={showEditSpecialtyDialog}
          onClose={() => setShowEditSpecialtyDialog(false)}
          specialty={(() => {
            const matchingFacilitySpecialty = facilitySpecialtyData.results?.find(
              (fs) => fs.specialty === watchedSpecialty?.value
            );

            if (matchingFacilitySpecialty) {
              return {
                ...matchingFacilitySpecialty,
                title: watchedSpecialty?.description || matchingFacilitySpecialty.specialty,
              };
            } else {
              return {
                id: '',
                specialty: watchedSpecialty?.value || '',
                title: watchedSpecialty?.description || watchedSpecialty?.value || '',
              };
            }
          })()}
          onSave={async (specialtyId, dynamicFields) => {
            return Promise.resolve();
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['facilitySpecialty'] });
            setShowEditSpecialtyDialog(false);
          }}
        />
      )}
      <UnsavedChangesFormPrompt
        hasUnsavedChanges={form.formState.isDirty && !isSubmitting && !isDiscarding}
      />
    </>
  );
};

export default OpportunityForm;
